import { IconButton, Stack, Menu, MenuItem } from '@mui/material';
import {
  TextB,
  TextH,
  TextItalic,
  ListBullets,
  ListNumbers,
  Link as LinkIcon,
  Image,
} from '@phosphor-icons/react';
import { useCurrentEditor } from '@tiptap/react';
import { useState } from 'react';
import config from '../../config';
import { Api, useApiCallable } from '../../api';
import { serverImageUrl } from '../../util';
import { CustomImageAttributes } from './TiptapEditor';

export default function FixedMenu() {
  const { editor } = useCurrentEditor();
  const callApi = useApiCallable();
  const [headingAnchorEl, setHeadingAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const headingMenuOpen = Boolean(headingAnchorEl);

  const handleHeadingClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHeadingAnchorEl(event.currentTarget);
  };

  const handleHeadingClose = () => {
    setHeadingAnchorEl(null);
  };

  // Determine if any heading is active (only h2 and h3)
  const isHeadingActive =
    editor?.isActive('heading', { level: 2 }) ||
    editor?.isActive('heading', { level: 3 });

  // Get the active heading level (or null if no heading is active)
  // @ts-expect-error TODO why not used?
  const getActiveHeadingLevel = () => {
    for (let i = 2; i <= 3; i++) {
      if (editor?.isActive('heading', { level: i })) {
        return i;
      }
    }
    return null;
  };

  // const activeHeadingLevel = getActiveHeadingLevel();

  return (
    <Stack
      direction="row"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'background.paper',
        pt: 0.75,
      }}
      gap={0.25}
    >
      <IconButton
        onClick={() => {
          editor?.chain().focus().toggleBold().run();
        }}
        color={editor?.isActive('bold') ? 'primary' : 'default'}
      >
        <TextB />
      </IconButton>
      <IconButton
        onClick={() => {
          editor?.chain().focus().toggleItalic().run();
        }}
        color={editor?.isActive('italic') ? 'primary' : 'default'}
      >
        <TextItalic />
      </IconButton>
      {/* <Button
        onClick={handleHeadingClick}
        color={isHeadingActive ? 'primary' : 'inherit'}
        // sx={{
        //   minWidth: 'auto',
        //   px: 1,
        //   display: 'flex',
        //   alignItems: 'center',
        //   gap: 0.5,
        // }}
        startIcon={<TextH />}
        endIcon={<CaretDown />}
      >
      </Button> */}
      <IconButton
        onClick={handleHeadingClick}
        color={isHeadingActive ? 'primary' : 'default'}
      >
        <TextH />
      </IconButton>
      <IconButton
        onClick={() => {
          editor?.chain().focus().toggleBulletList().run();
        }}
        color={editor?.isActive('bulletList') ? 'primary' : 'default'}
      >
        <ListBullets />
      </IconButton>
      <IconButton
        onClick={() => {
          editor?.chain().focus().toggleOrderedList().run();
        }}
        color={editor?.isActive('orderedList') ? 'primary' : 'default'}
      >
        <ListNumbers />
      </IconButton>
      <IconButton
        onClick={() => {
          editor
            ?.chain()
            .focus()
            .setLink({ href: 'https://www.google.com' })
            .run();
        }}
        color={editor?.isActive('link') ? 'primary' : 'default'}
      >
        <LinkIcon />
      </IconButton>
      <IconButton
        onClick={async () => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = config.ACCEPTED_MEDIA_TYPES.IMAGE_TYPE.join(',');

          input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
              try {
                // Create temporary local URL for immediate preview
                const tempUrl = URL.createObjectURL(file);

                // Insert the image first
                // editor
                //   ?.chain()
                //   .focus()
                //   .setImage({
                //     src: tempUrl,
                //   })
                //   .run();

                // Upload in background
                const response = await callApi<
                  Api.MediaUpload.BaseView,
                  object
                >('/media-uploads', {
                  method: 'POST',
                  uploadFile: file,
                  formBody: { type: 'content' },
                });

                if (response?.data) {
                  // Find the image node and update its attributes
                  const mediaId = response.data.id;
                  const serverUrl = serverImageUrl({ id: mediaId });

                  const imageAttributes: CustomImageAttributes = {
                    src: serverUrl,
                    mediaId,
                    caption: '',
                  };

                  editor?.chain().focus().setImage(imageAttributes).run();
                }

                URL.revokeObjectURL(tempUrl);
              } catch (error) {
                console.error('Failed to upload image:', error);
              }
            }
          };

          input.click();
        }}
        color={editor?.isActive('image') ? 'primary' : 'default'}
      >
        <Image />
      </IconButton>
      <Menu
        anchorEl={headingAnchorEl}
        open={headingMenuOpen}
        onClose={handleHeadingClose}
      >
        {[2, 3].map((level) => (
          <MenuItem
            key={level}
            onClick={() => {
              editor
                ?.chain()
                .focus()
                .toggleHeading({ level: level as any })
                .run();
              handleHeadingClose();
            }}
            selected={editor?.isActive('heading', { level })}
          >
            H{level}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            editor?.chain().focus().setParagraph().run();
            handleHeadingClose();
          }}
          selected={editor?.isActive('paragraph')}
        >
          Paragraph
        </MenuItem>
      </Menu>
    </Stack>
  );
}
